import React, { useState, useEffect } from 'react';

// Fixed reason text values
const REASON_TEXTS = {
  capacity: 'We are operating at maximum capacity based on licensing requirements',
  staffing: 'We have open beds but are experiencing a staffing shortage',
  closure: 'Program / service closure or limitations'
};

// Helper to get reason key from stored text
const getReasonKey = (text) => {
  for (const [key, value] of Object.entries(REASON_TEXTS)) {
    if (text === value) return key;
  }
  return 'other';
};

// Helper to check if text is a fixed reason
const isFixedReason = (text) => {
  return Object.values(REASON_TEXTS).includes(text);
};

const DataEntry = ({ userInfo, facilityInfo, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [facilityDetails, setFacilityDetails] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showGlossary, setShowGlossary] = useState(false);

  // Form data state with new field structure
  const [formData, setFormData] = useState({
    state_youth_detention: '0',
    state_youth_residential: '0',
    other_youth_detention: '0',
    other_youth_residential: '0',
    state_availability_detention: '0',
    state_availability_residential: '0',
    other_availability_detention: '0',
    other_availability_residential: '0',
    waitlist: '0',
    accepting_referrals: '',
    not_accepting_reason: '',
    not_accepting_reason_other_text: ''
  });

  useEffect(() => {
    if (facilityInfo) {
      fetchFacilityInfo(facilityInfo.provider_id);
      fetchLastUpdate(facilityInfo.provider_id);
    }
  }, [facilityInfo]);

  const fetchFacilityInfo = async (providerId) => {
    try {
      const response = await fetch(`/api/facility-info/${providerId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch facility info');
      }
      const data = await response.json();
      setFacilityDetails(data);
    } catch (err) {
      console.error('Error fetching facility info:', err);
    }
  };

  const fetchLastUpdate = async (providerId) => {
    try {
      const response = await fetch(`/api/last-update/${providerId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch last update');
      }
      const data = await response.json();
      if (data && data.lastUpdate) {
        const lastUpdateData = data.lastUpdate;
        setLastUpdate(lastUpdateData);

        // Set form data with last update values, defaulting to '0' if null/undefined
        setFormData({
          state_youth_detention: lastUpdateData.state_youth_detention?.toString() || '0',
          state_youth_residential: lastUpdateData.state_youth_residential?.toString() || '0',
          other_youth_detention: lastUpdateData.other_youth_detention?.toString() || '0',
          other_youth_residential: lastUpdateData.other_youth_residential?.toString() || '0',
          state_availability_detention: lastUpdateData.state_availability_detention?.toString() || '0',
          state_availability_residential: lastUpdateData.state_availability_residential?.toString() || '0',
          other_availability_detention: lastUpdateData.other_availability_detention?.toString() || '0',
          other_availability_residential: lastUpdateData.other_availability_residential?.toString() || '0',
          waitlist: lastUpdateData.waitlist?.toString() || '0',
          accepting_referrals: lastUpdateData.accepting_referrals === true ? 'yes' : (lastUpdateData.accepting_referrals === false ? 'no' : ''),
          not_accepting_reason: lastUpdateData.not_accepting_reasons ? getReasonKey(lastUpdateData.not_accepting_reasons) : '',
          not_accepting_reason_other_text: lastUpdateData.not_accepting_reasons && !isFixedReason(lastUpdateData.not_accepting_reasons) ? lastUpdateData.not_accepting_reasons : ''
        });
      }
    } catch (err) {
      console.error('Error fetching last update:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle numeric fields - only allow numbers
    const numericFields = [
      'state_youth_detention', 'state_youth_residential',
      'other_youth_detention', 'other_youth_residential',
      'state_availability_detention', 'state_availability_residential',
      'other_availability_detention', 'other_availability_residential',
      'waitlist'
    ];

    if (numericFields.includes(name)) {
      if (value === '' || /^\d+$/.test(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };

  const handleRadioChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      accepting_referrals: value,
      // Reset reason when changing to 'yes'
      not_accepting_reason: value === 'yes' ? '' : prev.not_accepting_reason,
      not_accepting_reason_other_text: value === 'yes' ? '' : prev.not_accepting_reason_other_text
    }));
  };

  const handleReasonChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      not_accepting_reason: value,
      // Clear other_text when selecting a different reason
      not_accepting_reason_other_text: value !== 'other' ? '' : prev.not_accepting_reason_other_text
    }));
  };

  const handleOtherTextChange = (e) => {
    setFormData(prev => ({
      ...prev,
      not_accepting_reason_other_text: e.target.value
    }));
  };

  const validateForm = () => {
    const validationErrors = [];
    const capacity = facilityInfo?.capacity || 0;

    // Parse all numeric values
    const stateYouthDetention = parseInt(formData.state_youth_detention) || 0;
    const stateYouthResidential = parseInt(formData.state_youth_residential) || 0;
    const otherYouthDetention = parseInt(formData.other_youth_detention) || 0;
    const otherYouthResidential = parseInt(formData.other_youth_residential) || 0;
    const stateAvailDetention = parseInt(formData.state_availability_detention) || 0;
    const stateAvailResidential = parseInt(formData.state_availability_residential) || 0;
    const otherAvailDetention = parseInt(formData.other_availability_detention) || 0;
    const otherAvailResidential = parseInt(formData.other_availability_residential) || 0;
    const waitlist = parseInt(formData.waitlist) || 0;

    // Validate non-negative
    if (stateYouthDetention < 0) validationErrors.push('State Youth Detention must be non-negative.');
    if (stateYouthResidential < 0) validationErrors.push('State Youth Residential must be non-negative.');
    if (otherYouthDetention < 0) validationErrors.push('Other Youth Detention must be non-negative.');
    if (otherYouthResidential < 0) validationErrors.push('Other Youth Residential must be non-negative.');
    if (stateAvailDetention < 0) validationErrors.push('State Availability Detention must be non-negative.');
    if (stateAvailResidential < 0) validationErrors.push('State Availability Residential must be non-negative.');
    if (otherAvailDetention < 0) validationErrors.push('Other Availability Detention must be non-negative.');
    if (otherAvailResidential < 0) validationErrors.push('Other Availability Residential must be non-negative.');
    if (waitlist < 0) validationErrors.push('Waitlist must be non-negative.');

    // Validate accepting_referrals is selected
    if (!formData.accepting_referrals) {
      validationErrors.push('Please indicate whether you are accepting new referrals.');
    }

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const prepareSubmitData = () => {
    return {
      provider_id: facilityInfo.provider_id,
      state_youth_detention: formData.state_youth_detention || '0',
      state_youth_residential: formData.state_youth_residential || '0',
      other_youth_detention: formData.other_youth_detention || '0',
      other_youth_residential: formData.other_youth_residential || '0',
      state_availability_detention: formData.state_availability_detention || '0',
      state_availability_residential: formData.state_availability_residential || '0',
      other_availability_detention: formData.other_availability_detention || '0',
      other_availability_residential: formData.other_availability_residential || '0',
      waitlist: formData.waitlist || '0',
      accepting_referrals: formData.accepting_referrals === 'yes',
      not_accepting_reasons: formData.accepting_referrals === 'no'
        ? (formData.not_accepting_reason === 'other'
            ? formData.not_accepting_reason_other_text
            : REASON_TEXTS[formData.not_accepting_reason] || null)
        : null
    };
  };

  const handleNoChanges = async () => {
    console.log('=== NO CHANGES SUBMISSION ===');
    setLoading(true);
    setErrors([]);

    // Still need to validate referrals selection
    if (!formData.accepting_referrals) {
      setErrors(['Please indicate whether you are accepting new referrals.']);
      setLoading(false);
      return;
    }

    try {
      const submitData = prepareSubmitData();
      console.log('No changes - submitting data:', submitData);

      const response = await fetch('/api/submit-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      const result = await response.json();
      console.log('No changes - server response:', result);

      if (result.success) {
        const submissionData = {
          provider_name: facilityInfo.provider_name,
          timestamp: formatTimestampForBackend()
        };
        sessionStorage.setItem('submissionData', JSON.stringify(submissionData));
        setTimeout(() => {
          onSuccess();
        }, 100);
      } else {
        setErrors(['Failed to submit data. Please try again.']);
      }
    } catch (err) {
      setErrors(['An error occurred while submitting data. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== FORM SUBMISSION START ===');
    console.log('Form Data:', formData);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = prepareSubmitData();
      console.log('Submitting data:', submitData);

      const response = await fetch('/api/submit-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      const result = await response.json();
      console.log('Server response:', result);

      if (result.success) {
        const submissionData = {
          provider_name: facilityInfo.provider_name,
          timestamp: formatTimestampForBackend()
        };
        sessionStorage.setItem('submissionData', JSON.stringify(submissionData));
        setTimeout(() => {
          onSuccess();
        }, 100);
      } else {
        setErrors(['Failed to submit data. Please try again.']);
      }
    } catch (err) {
      setErrors(['An error occurred while submitting data. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const formatLastUpdateDate = (dateString) => {
    if (!dateString) return null;

    const isoDateString = dateString.replace(' ', 'T');
    const date = new Date(isoDateString);

    if (isNaN(date.getTime())) return null;
    if (date.getFullYear() === 1999 && date.getMonth() === 0 && date.getDate() === 1) return null;

    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    return date.toLocaleDateString('en-US', options);
  };

  const formatTimestampForBackend = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const year = now.getFullYear();
    const hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  // Parse other_notes by splitting on "* " and rendering each as a bullet
  const renderOtherNotes = (notes) => {
    if (!notes) return null;

    // Split by "* " and filter out empty strings
    const noteParts = notes.split('* ').filter(part => part.trim() !== '');

    if (noteParts.length === 0) return null;

    return noteParts.map((note, index) => (
      <li key={index} className="text-type-6 text-body">{note.trim()}</li>
    ));
  };

  // Render facility description in bulleted format
  const renderFacilityDescription = () => {
    if (!facilityDetails) return <p className="text-type-6 text-body">No description available</p>;

    return (
      <ul className="facility-description-list" style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
        <li className="text-type-6 text-body">
          Ages Served: {facilityDetails.age_from || 'N/A'}-{facilityDetails.age_thru || 'N/A'}
        </li>
        <li className="text-type-6 text-body">
          Genders: {facilityDetails.sex || 'N/A'}
        </li>
        <li className="text-type-6 text-body">
          Non-Secure: {facilityDetails.non_secure ? 'YES' : 'NO'}
        </li>
        <li className="text-type-6 text-body">
          Secure: {facilityDetails.secure ? 'YES' : 'NO'}
        </li>
        <li className="text-type-6 text-body">
          Length of Stay: {facilityDetails.length_of_stay || 'N/A'}
        </li>
        {facilityDetails.allow_18yo && (
          <li className="text-type-6 text-body">
            Allows youth to remain at the facility after turning 18
          </li>
        )}
        {facilityDetails.accept_out_of_county_youth && (
          <li className="text-type-6 text-body">
            Accepts out-of-county youth
          </li>
        )}
        {renderOtherNotes(facilityDetails.other_notes)}
      </ul>
    );
  };

  if (!facilityInfo) {
    return (
      <div className="main-content">
        <div className="text-center">
          <h1 className="text-type-2 text-header">Loading...</h1>
        </div>
      </div>
    );
  }

  const displayFacilityInfo = facilityDetails || facilityInfo;
  const lastUpdateFormatted = formatLastUpdateDate(lastUpdate?.time_of_update);

  return (
    <div className="data-entry-container">
      {/* Welcome Message */}
      <h1 className="text-type-1-bold text-header" style={{ textAlign: 'left', marginBottom: '10px', marginTop: '0' }}>
        Welcome, {facilityInfo.provider_name}
      </h1>

      {/* Last Updated Info */}
      <div className="text-type-6 text-caption" style={{ fontStyle: 'italic', marginBottom: '20px' }}>
        {lastUpdateFormatted ? (
          <p>Last updated on {lastUpdateFormatted}</p>
        ) : (
          <p>No census data found. Please update below.</p>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="two-column-layout">
        {/* Left Column - Facility Information */}
        <div className="facility-info-column">
          <h2 className="text-type-2-bold text-header">Facility Information</h2>

          <div className="facility-details">
            <h3 className="text-type-3 text-header">{displayFacilityInfo.provider_name}</h3>
            <p className="text-type-6 text-body"><strong>Administrator:</strong> {displayFacilityInfo.chief_admin || 'N/A'}</p>
            <p className="text-type-6 text-body"><strong>Admin Email:</strong> {displayFacilityInfo.chief_admin_email || 'N/A'}</p>
            <p className="text-type-6 text-body"><strong>Phone:</strong> {displayFacilityInfo.phone || 'N/A'}</p>
            <p className="text-type-6 text-body"><strong>Total Licensed Capacity:</strong> {displayFacilityInfo.capacity}</p>

            <div className="facility-description">
              <h4 className="text-type-4 text-header">Facility Description:</h4>
              {renderFacilityDescription()}
            </div>

            <div className="note">
              <p className="text-type-6 text-body"><strong>Note:</strong> If any information is incorrect, please request a change with <a href="https://forms.office.com/Pages/ResponsePage.aspx?id=h3D71Xc3rUKWaoku9HIl0fuF_mPt2UNPqcjAvZ56tvJUNzhQMTk1RERSWU9BNDlJVkI0TlcxRkZPOCQlQCN0PWcu" target="_blank" rel="noopener noreferrer" className="text-interactive">Data Change Request Form</a>. For assistance, contact MDHHS-JJReform@michigan.gov.</p>
            </div>

            {/* Glossary */}
            <div className="glossary-section">
              <button
                type="button"
                className="glossary-toggle text-type-5"
                onClick={() => setShowGlossary(!showGlossary)}
              >
                <strong>Glossary of Terms</strong> {showGlossary ? '▼' : '▶'}
              </button>

              {showGlossary && (
                <div className="glossary-content">
                  <p className="text-type-6 text-body">This glossary provides standard definitions for terms used in the Bed Management Reporting Tool to support consistent data entry and interpretation across juvenile facilities and partner agencies.</p>

                  <dl className="glossary-definitions">
                    <dt className="text-type-6 text-header"><strong>Available Bed</strong></dt>
                    <dd className="text-type-7 text-body">A bed that is not currently occupied and is ready to be used for a new placement, based on staffing, facility policy, and licensing requirements.</dd>

                    <dt className="text-type-6 text-header"><strong>Capacity</strong></dt>
                    <dd className="text-type-7 text-body">The maximum number of youth a facility is licensed or approved to serve at any given time, as determined by the overseeing agency or regulatory body.</dd>

                    <dt className="text-type-6 text-header"><strong>Census</strong></dt>
                    <dd className="text-type-7 text-body">The total number of youth physically present in a facility at a specific point in time, typically recorded at a standardized time each day (e.g., 11:59 p.m.).</dd>

                    <dt className="text-type-6 text-header"><strong>Occupied Bed</strong></dt>
                    <dd className="text-type-7 text-body">A bed currently in use by a youth residing in the facility.</dd>

                    <dt className="text-type-6 text-header"><strong>Private Facility</strong></dt>
                    <dd className="text-type-7 text-body">A juvenile facility operated by a private entity or nonprofit organization, often under contract with or licensed by a government agency.</dd>

                    <dt className="text-type-6 text-header"><strong>Public Facility</strong></dt>
                    <dd className="text-type-7 text-body">A juvenile facility operated or managed by a government agency, such as a local, county, or state government.</dd>

                    <dt className="text-type-6 text-header"><strong>Non-Secure Facility</strong></dt>
                    <dd className="text-type-7 text-body">A residential setting for youth that does not use physical security features like locked doors or fences. These may include group homes, shelter care, or foster care programs, with staff providing supervision and structure.</dd>

                    <dt className="text-type-6 text-header"><strong>Residential Treatment</strong></dt>
                    <dd className="text-type-7 text-body">Long-term rehabilitation for juveniles requiring therapy and structured care. Typically, 6-12 months depending on treatment needs and progress. Structured environment and can be secure, staff secure, or non-secure settings. Licensed by the State of Michigan as a court-operated facility and operated by the court or licensed as a child-caring institution operated by the private agency or by the State of Michigan.</dd>

                    <dt className="text-type-6 text-header"><strong>Secure Detention</strong></dt>
                    <dd className="text-type-7 text-body">Short-term confinement for juveniles who present a public safety risk awaiting court hearings, temporary placement while charges are resolved. Facilities are secure and are a highly structured environment. Licensed by the State of Michigan as a court-operated facility or licensed as a child-caring institution operated by the county.</dd>

                    <dt className="text-type-6 text-header"><strong>Court Ward</strong></dt>
                    <dd className="text-type-7 text-body">A juvenile under the jurisdiction of the local county court.</dd>

                    <dt className="text-type-6 text-header"><strong>State Ward</strong></dt>
                    <dd className="text-type-7 text-body">A juvenile committed to the state's care and custody with the Michigan Department of Health and Human Services (MDHHS) but under the jurisdiction of the local county court.</dd>

                    <dt className="text-type-6 text-header"><strong>Dual Ward</strong></dt>
                    <dd className="text-type-7 text-body">A youth who is involved in both child welfare and the juvenile justice systems at the same time. Coordination between agencies is essential to meet legal and services needs.</dd>
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Data Entry Form */}
        <div className="form-column">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 className="text-type-2-bold text-header">Data Entry Form</h2>
            <button
              type="button"
              className="no-changes-btn"
              onClick={handleNoChanges}
              disabled={loading}
              style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500' }}
            >
              No New Changes
            </button>
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="alert alert-error" role="alert">
              <p className="text-type-5 text-danger">Please fix the following errors before submitting:</p>
              <ul>
                {errors.map((error, index) => (
                  <li key={index} className="text-type-6 text-body">{error}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Section 1: Current Census */}
            <div className="form-section">
              <h3 className="text-type-4 text-header" style={{ marginBottom: '15px', borderBottom: '1px solid #e0e0e0', paddingBottom: '10px' }}>Current Census</h3>

              {/* MDHHS State Youth */}
              <div className="form-group">
                <label className="form-label text-type-5 text-header">
                  <strong>Number of MDHHS State youth currently in the facility.</strong>
                </label>
                <p className="field-description text-type-6 text-caption">
                  Enter the current number of MDHHS state supervised youth, dual wards and temporary court wards placed in your facility.
                </p>
                <div className="input-row" style={{ display: 'flex', gap: '20px' }}>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="state_youth_detention" className="text-type-6">Detention</label>
                    <input
                      type="text"
                      id="state_youth_detention"
                      name="state_youth_detention"
                      className="form-control"
                      value={formData.state_youth_detention}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="state_youth_residential" className="text-type-6">Residential Treatment</label>
                    <input
                      type="text"
                      id="state_youth_residential"
                      name="state_youth_residential"
                      className="form-control"
                      value={formData.state_youth_residential}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Other Youth */}
              <div className="form-group">
                <label className="form-label text-type-5 text-header">
                  <strong>Number of Other youth currently in the facility.</strong>
                </label>
                <p className="field-description text-type-6 text-caption">
                  Enter the current number of youth from other sources (out-of-state placements, private placements, court supervised youth, etc.) who are in your facility.
                </p>
                <div className="input-row" style={{ display: 'flex', gap: '20px' }}>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="other_youth_detention" className="text-type-6">Detention</label>
                    <input
                      type="text"
                      id="other_youth_detention"
                      name="other_youth_detention"
                      className="form-control"
                      value={formData.other_youth_detention}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="other_youth_residential" className="text-type-6">Residential Treatment</label>
                    <input
                      type="text"
                      id="other_youth_residential"
                      name="other_youth_residential"
                      className="form-control"
                      value={formData.other_youth_residential}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Bed Availability */}
            <div className="form-section">
              <h3 className="text-type-4 text-header" style={{ marginBottom: '15px', borderBottom: '1px solid #e0e0e0', paddingBottom: '10px' }}>Bed Availability</h3>

              {/* MDHHS State Availability */}
              <div className="form-group">
                <label className="form-label text-type-5 text-header">
                  <strong>Number of open beds currently staffed AND available for MDHHS State youth</strong>
                </label>
                <p className="field-description text-type-6 text-caption">
                  Enter the number of available beds in your facility for MDHHS supervised youth.
                </p>
                <div className="input-row" style={{ display: 'flex', gap: '20px' }}>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="state_availability_detention" className="text-type-6">Detention</label>
                    <input
                      type="text"
                      id="state_availability_detention"
                      name="state_availability_detention"
                      className="form-control"
                      value={formData.state_availability_detention}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="state_availability_residential" className="text-type-6">Residential Treatment</label>
                    <input
                      type="text"
                      id="state_availability_residential"
                      name="state_availability_residential"
                      className="form-control"
                      value={formData.state_availability_residential}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Other Availability */}
              <div className="form-group">
                <label className="form-label text-type-5 text-header">
                  <strong>Number of open beds that currently staffed AND available for Other youth.</strong>
                </label>
                <p className="field-description text-type-6 text-caption">
                  Enter the number of available beds for court and county supervised youth.
                </p>
                <div className="input-row" style={{ display: 'flex', gap: '20px' }}>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="other_availability_detention" className="text-type-6">Detention</label>
                    <input
                      type="text"
                      id="other_availability_detention"
                      name="other_availability_detention"
                      className="form-control"
                      value={formData.other_availability_detention}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-field" style={{ flex: 1 }}>
                    <label htmlFor="other_availability_residential" className="text-type-6">Residential Treatment</label>
                    <input
                      type="text"
                      id="other_availability_residential"
                      name="other_availability_residential"
                      className="form-control"
                      value={formData.other_availability_residential}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Waitlist */}
            <div className="form-section">
              <h3 className="text-type-4 text-header" style={{ marginBottom: '15px', borderBottom: '1px solid #e0e0e0', paddingBottom: '10px' }}>Waitlist</h3>

              <div className="form-group">
                <label htmlFor="waitlist" className="form-label text-type-5 text-header">
                  <strong>Number of Youth on Waiting List:</strong>
                </label>
                <p className="field-description text-type-6 text-caption">
                  Enter the total number of youth who are waiting for placement but cannot currently be admitted due to space limitations or other constraints.
                </p>
                <input
                  type="text"
                  id="waitlist"
                  name="waitlist"
                  className="form-control"
                  value={formData.waitlist}
                  onChange={handleInputChange}
                  style={{ maxWidth: '200px' }}
                />
              </div>
            </div>

            {/* Section 4: Referrals */}
            <div className="form-section">
              <h3 className="text-type-4 text-header" style={{ marginBottom: '15px', borderBottom: '1px solid #e0e0e0', paddingBottom: '10px' }}>Referrals</h3>

              <div className="form-group">
                <label className="form-label text-type-5 text-header">
                  <strong>Are you currently accepting new referrals?</strong>
                </label>
                <div className="radio-group" style={{ marginTop: '10px' }}>
                  <div className="radio-option" style={{ marginBottom: '2px' }}>
                    <input
                      type="radio"
                      id="referrals-yes"
                      name="accepting_referrals"
                      value="yes"
                      checked={formData.accepting_referrals === 'yes'}
                      onChange={handleRadioChange}
                    />
                    <label htmlFor="referrals-yes" className="text-type-6" style={{ marginLeft: '8px' }}>
                      Yes, we are currently accepting new referrals
                    </label>
                  </div>
                  <div className="radio-option">
                    <input
                      type="radio"
                      id="referrals-no"
                      name="accepting_referrals"
                      value="no"
                      checked={formData.accepting_referrals === 'no'}
                      onChange={handleRadioChange}
                    />
                    <label htmlFor="referrals-no" className="text-type-6" style={{ marginLeft: '8px' }}>
                      No, we are not currently accepting new referrals
                    </label>
                  </div>
                </div>

                {/* Conditional Reasons Section */}
                {formData.accepting_referrals === 'no' && (
                  <div className="reason-group" style={{ marginTop: '15px', marginLeft: '20px', padding: '15px', backgroundColor: '#f8f8f8', borderRadius: '4px' }}>
                    <label className="form-label text-type-5" style={{ marginBottom: '12px', display: 'block' }}>
                      <strong>If No, please specify:</strong>
                    </label>

                    <div className="radio-option" style={{ marginBottom: '4px' }}>
                      <input
                        type="radio"
                        id="reason-capacity"
                        name="not_accepting_reason"
                        value="capacity"
                        checked={formData.not_accepting_reason === 'capacity'}
                        onChange={handleReasonChange}
                      />
                      <label htmlFor="reason-capacity" className="text-type-6" style={{ marginLeft: '8px' }}>
                        We are operating at maximum capacity based on licensing requirements
                      </label>
                    </div>

                    <div className="radio-option" style={{ marginBottom: '4px' }}>
                      <input
                        type="radio"
                        id="reason-staffing"
                        name="not_accepting_reason"
                        value="staffing"
                        checked={formData.not_accepting_reason === 'staffing'}
                        onChange={handleReasonChange}
                      />
                      <label htmlFor="reason-staffing" className="text-type-6" style={{ marginLeft: '8px' }}>
                        We have open beds but are experiencing a staffing shortage
                      </label>
                    </div>

                    <div className="radio-option" style={{ marginBottom: '4px' }}>
                      <input
                        type="radio"
                        id="reason-closure"
                        name="not_accepting_reason"
                        value="closure"
                        checked={formData.not_accepting_reason === 'closure'}
                        onChange={handleReasonChange}
                      />
                      <label htmlFor="reason-closure" className="text-type-6" style={{ marginLeft: '8px' }}>
                        Program / service closure or limitations
                      </label>
                    </div>

                    <div className="radio-option" style={{ marginBottom: '4px' }}>
                      <input
                        type="radio"
                        id="reason-other"
                        name="not_accepting_reason"
                        value="other"
                        checked={formData.not_accepting_reason === 'other'}
                        onChange={handleReasonChange}
                      />
                      <label htmlFor="reason-other" className="text-type-6" style={{ marginLeft: '8px' }}>
                        Other
                      </label>
                    </div>

                    {formData.not_accepting_reason === 'other' && (
                      <div className="other-explanation" style={{ marginTop: '10px', marginLeft: '24px' }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Provide brief explanation"
                          value={formData.not_accepting_reason_other_text}
                          onChange={handleOtherTextChange}
                          style={{ maxWidth: '400px' }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DataEntry;
